import React, { createContext, useContext, useEffect, useState } from "react";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { DeviceManager } from "../utils/DeviceManager";

interface DevicesContextType {
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  selectedAudioInputDevice: string | null;
  selectedVideoInputDevice: string | null;
  selectedAudioOutputDevice: string | null;
  isDevicesLoading: boolean;
  listAndSelectDevices: (deviceLabels: DeviceLabels) => Promise<void>;
  selectAudioInputDevice: (deviceId: string) => Promise<void>;
  selectVideoInputDevice: (deviceId: string) => Promise<void>;
  selectAudioOutputDevice: (deviceId: string) => Promise<void>;
  forceRefreshDevices: () => Promise<void>;
}

const DevicesContext = createContext<DevicesContextType | null>(null);

export const DevicesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [deviceManager] = useState(() => new DeviceManager());
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    []
  );
  const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>(
    []
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState<
    string | null
  >(null);
  const [selectedVideoInputDevice, setSelectedVideoInputDevice] = useState<
    string | null
  >(null);
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState<
    string | null
  >(null);
  const [isDevicesLoading, setIsDevicesLoading] = useState(true);

  const listAndSelectDevices = async (deviceLabels: DeviceLabels) => {
    setIsDevicesLoading(true);
    try {
      await deviceManager.listAndSelectDevices(deviceLabels);
      setAudioInputDevices(deviceManager.getAudioInputDevices());
      setVideoInputDevices(deviceManager.getVideoInputDevices());
      setAudioOutputDevices(deviceManager.getAudioOutputDevices());
      
      const newSelectedAudioInput = deviceManager.getSelectedAudioInputDevice();
      const newSelectedVideoInput = deviceManager.getSelectedVideoInputDevice();
      const newSelectedAudioOutput = deviceManager.getSelectedAudioOutputDevice();
      
      setSelectedAudioInputDevice(newSelectedAudioInput);
      setSelectedVideoInputDevice(newSelectedVideoInput);
      setSelectedAudioOutputDevice(newSelectedAudioOutput);
      
      // Log when devices are auto-selected to help with debugging
      if (newSelectedVideoInput) {
        console.log('DevicesProvider: Video input device set to:', newSelectedVideoInput);
      }
      if (newSelectedAudioInput) {
        console.log('DevicesProvider: Audio input device set to:', newSelectedAudioInput);
      }
    } finally {
      setIsDevicesLoading(false);
    }
  };

  const selectAudioInputDevice = async (deviceId: string) => {
    await deviceManager.selectAudioInputDevice(deviceId);
    setSelectedAudioInputDevice(deviceId);
  };

  const selectVideoInputDevice = async (deviceId: string) => {
    await deviceManager.selectVideoInputDevice(deviceId);
    setSelectedVideoInputDevice(deviceId);
  };

  const selectAudioOutputDevice = async (deviceId: string) => {
    await deviceManager.selectAudioOutputDevice(deviceId);
    setSelectedAudioOutputDevice(deviceId);
  };

  const forceRefreshDevices = async () => {
    await deviceManager.forceRefreshDevices();
    setAudioOutputDevices(deviceManager.getAudioOutputDevices());
    const newSelectedAudioOutput = deviceManager.getSelectedAudioOutputDevice();
    setSelectedAudioOutputDevice(newSelectedAudioOutput);
  };

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = async () => {
      console.log('Device change detected, refreshing device list...');
      try {
        await listAndSelectDevices(DeviceLabels.AudioAndVideo);
        console.log('Device list refreshed successfully');
      } catch (error) {
        console.warn('Failed to refresh device list:', error);
      }
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [listAndSelectDevices]);

  const value = {
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    isDevicesLoading,
    listAndSelectDevices,
    selectAudioInputDevice,
    selectVideoInputDevice,
    selectAudioOutputDevice,
    forceRefreshDevices,
  };

  return (
    <DevicesContext.Provider value={value}>{children}</DevicesContext.Provider>
  );
};

export const useDevices = () => {
  const context = useContext(DevicesContext);
  if (!context) {
    throw new Error("useDevices must be used within a DevicesProvider");
  }
  return context;
};
